import React, { useEffect, useState } from "react";
import {
    Box, Button, Checkbox, Chip, Divider, FormControlLabel, IconButton,
    LinearProgress, MenuItem, Stack, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import FormModal from "../../../component/common/FormModal";
import ActionButton from "../../../component/common/ActionButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import AttachFileRoundedIcon from "@mui/icons-material/AttachFileRounded";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import { notify } from "../../../util/toast";
import { runApiAction, serverMsgOr } from "../../../util/apiFeedback";
import { calendarApi } from "../utils/calendarApi";
import { fmtSize, asList } from "../utils/calendarUtils";
import {
    applyRangeEndChange,
    applyRangeStartChange,
    endOfDayString,
    formatDate,
    formatDatetimeLocal,
    normalizeDatetimeSeconds,
    startOfDayString,
    today,
} from "../../../util/dateUtils";
import FormField from "../../../component/common/FormField";
import AttachmentPreviewList from "../../../component/common/AttachmentPreviewList";
import AppAutocomplete from "../../../component/common/AppAutocomplete";
import { CATEGORY_OPTIONS, DEFAULT_CATEGORY, categoryMeta } from "../constants/eventCategory";
import { getApiErrorMessage } from "../../../util/apiFeedback";

/**
 * 읽기전용 여부 → TextField 스프레드 props(입력만 막고 회색 처리는 하지 않는다).
 * 작성자가 아니면 값은 보되 수정만 못 하게 하는 화면이라, disabled(회색·포커스 불가)가 아니라
 * readOnly 로 둔다 — 회색으로 죽은 것처럼 보이면 "왜 안 보이지"라는 오해를 준다는 사용자 피드백.
 */
const readOnlyProps = (ro) => ({ slotProps: { htmlInput: { readOnly: ro } } });

export function EventDialog({ open, event, myDeptId, myDeptName, employees = [], onSave, onDelete, onClose, onDone }) {
    // 휴가·프로젝트는 캘린더가 소유하지 않는 합성 이벤트(결재/채널에서 라이브 조회) — 저장·삭제 대상이 아니다.
    // 이제 상세도 우측 사이드바가 아니라 이 모달로 보여주므로, 여기서 읽기전용으로 안전하게 표시해야 한다.
    const isSynthetic = !!(event?.isLeave || event?.isProject);
    // '수정 가능한 캘린더 행'인지 — 합성 이벤트는 id 가 "leave-.."/"channel-.." 문자열이라 편집/첨부 로드에서 제외.
    const isEdit = !!event?.id && !isSynthetic;
    // 작성자만 편집. 합성 이벤트·남의 일정은 읽기전용(모두 같은 화면을 보되 입력이 잠긴다).
    const isOwner = isSynthetic ? false : (isEdit ? event?._isOwner !== false : true);

    const [form, setForm] = useState({
        title: "", description: "", location: "",
        category: DEFAULT_CATEGORY,
        allDay: false,
        startAt: "", endAt: "",
        startDate: "", endDate: "",
        deptId: "",
        participants: [],   // [{ id, name }] — 참여자(공유 대상)
    });
    const [attachments, setAttachments] = useState([]);
    const [pendingFiles, setPendingFiles] = useState([]);
    const [saving, setSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = React.useRef(null);

    useEffect(() => {
        if (!open) return;
        const defaultDay = event?._defaultDate
            ? formatDate(event._defaultDate)
            : formatDate(today());

        setForm({
            title: event?.title || "",
            category: event?.category || DEFAULT_CATEGORY,
            description: event?.description || "",
            location: event?.location || "",
            allDay: event?.allDay || false,
            startAt: event?.startAt ? formatDatetimeLocal(event.startAt) : `${defaultDay}T09:00`,
            endAt:   event?.endAt   ? formatDatetimeLocal(event.endAt)   : `${defaultDay}T10:00`,
            startDate: event?.allDay && event?.startAt
                ? formatDate(event.startAt) : defaultDay,
            endDate: event?.allDay && event?.endAt
                ? formatDate(event.endAt) : defaultDay,
            deptId: event?.deptId ? String(event.deptId) : "",
            // 서버 응답 participants([{employeeId, name, deptName, positionName}]) → 폼용. 부서·직위도 표시에 쓴다.
            participants: Array.isArray(event?.participants)
                ? event.participants.map(p => ({ id: p.employeeId, name: p.name, deptName: p.deptName, positionName: p.positionName }))
                : [],
        });
        setPendingFiles([]);
        setUploadProgress(0);

        if (isEdit) {
            calendarApi.getAttachments(event.id)
                .then(r => setAttachments(asList(r, "attachments")))
                .catch(() => setAttachments([]));
        } else {
            setAttachments([]);
        }
    }, [open, event]);

    const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        setPendingFiles(p => [...p, ...files]);
        e.target.value = "";
    };

    const removePending = (idx) => setPendingFiles(p => p.filter((_, i) => i !== idx));

    const handleDeleteAttach = (attachId) => runApiAction({
        request: () => calendarApi.deleteAttachment(attachId),
        successMsg: "파일이 삭제되었습니다",
        errorMsg: serverMsgOr("파일 삭제 실패"),
        onSuccess: () => setAttachments(a => a.filter(x => x.id !== attachId)),
    });

    const handleSave = async () => {
        if (!form.title.trim()) { notify("제목을 입력하세요", "error"); return; }
        let startAt, endAt;
        if (form.allDay) {
            if (!form.startDate) { notify("날짜를 선택하세요", "error"); return; }
            startAt = startOfDayString(form.startDate);
            endAt   = form.endDate && form.endDate >= form.startDate
                ? endOfDayString(form.endDate)
                : endOfDayString(form.startDate);
        } else {
            if (!form.startAt) { notify("시작일시를 입력하세요", "error"); return; }
            startAt = normalizeDatetimeSeconds(form.startAt);
            const rawEnd = form.endAt || form.startAt;
            endAt = normalizeDatetimeSeconds(rawEnd);
        }

        const payload = {
            title: form.title, description: form.description || null,
            location: form.location || null,
            startAt, endAt, allDay: form.allDay,
            category: form.category || DEFAULT_CATEGORY,
            deptId: form.deptId ? Number(form.deptId) : null,
            // 참여자 id 목록만 전송(이름은 서버가 스냅샷). 중복은 서버가 제거하지만 여기서도 걸러 보낸다.
            participantIds: [...new Set((form.participants || []).map(p => p.id).filter(Boolean))],
        };

        setSaving(true);
        try {
            const eventId = await onSave(isEdit ? event.id : null, payload);

            if (pendingFiles.length > 0) {
                for (let i = 0; i < pendingFiles.length; i++) {
                    setUploadProgress(Math.round((i / pendingFiles.length) * 100));
                    try {
                        await calendarApi.uploadAttachment(eventId, pendingFiles[i]);
                    } catch (e) {
                        notify(`${pendingFiles[i].name} ${getApiErrorMessage(e, "업로드 실패")}`, "error");
                    }
                }
                setUploadProgress(100);
            }
            notify(isEdit ? "일정이 수정되었습니다" : "일정이 추가되었습니다", "success");
            onDone();
        } catch (e) {
            notify(getApiErrorMessage(e, "저장 실패"), "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <FormModal
            open={open}
            onClose={onClose}
            title={!isOwner ? "일정 상세" : (isEdit ? "일정 수정" : "일정 추가")}
            subtitle={isOwner ? undefined : (isSynthetic ? (event?.isLeave ? "결재 완료된 휴가 일정" : "프로젝트 채널 일정") : "읽기 전용")}
            maxWidth="md"
            saving={saving}
            onSave={isOwner ? handleSave : undefined}
            saveLabel={isEdit ? "저장" : "추가"}
            cancelLabel={isOwner ? "취소" : "닫기"}
            footerExtras={
                isEdit && isOwner ? (
                    <ActionButton variant="delete" onClick={() => onDelete(event.id)} disabled={saving} />
                ) : null
            }
        >
            {saving && (
                <LinearProgress
                    sx={{ mb: 2, borderRadius: 1 }}
                    variant={uploadProgress > 0 ? "determinate" : "indeterminate"}
                    value={uploadProgress}
                />
            )}

                <Stack spacing={2.5}>
                    <FormField label="제목" required>
                        <TextField
                            size="small" value={form.title}
                            onChange={e => set("title", e.target.value)}
                            fullWidth autoFocus={isOwner}
                            placeholder="제목을 입력하세요"
                            {...readOnlyProps(!isOwner)}
                        />
                    </FormField>

                    {/* 휴가는 유형 대신 '휴가 유형'(결재값)을 읽기전용으로 보여준다 — 일정 유형 선택은 의미가 없다. */}
                    {event?.isLeave ? (
                        <FormField label="휴가 유형">
                            <TextField
                                size="small" fullWidth value={event?.leaveType || "휴가"}
                                {...readOnlyProps(true)}
                            />
                        </FormField>
                    ) : (
                        /* 일정 유형 — 캘린더 색상과 체크박스 필터가 이 값 하나를 본다. */
                        <FormField label="일정 유형" required>
                            <TextField
                                select size="small" fullWidth
                                value={form.category}
                                onChange={e => set("category", e.target.value)}
                                slotProps={{ select: { readOnly: !isOwner } }}
                            >
                                {CATEGORY_OPTIONS.map(opt => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        <Stack direction="row" alignItems="center" spacing={1}>
                                            <Box sx={{
                                                width: 10, height: 10, borderRadius: "50%",
                                                bgcolor: categoryMeta(opt.value).bar, flexShrink: 0,
                                            }} />
                                            <span>{opt.label}</span>
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </TextField>
                        </FormField>
                    )}

                    <FormField label="장소">
                        <TextField
                            size="small" value={form.location} fullWidth
                            onChange={e => set("location", e.target.value)}
                            placeholder={isOwner ? "예: 4층 컨퍼런스룸 / 서울특별시 종로구 종로 14 / 온라인(Teams)" : ""}
                            slotProps={{ htmlInput: { readOnly: !isOwner, maxLength: 300 } }}
                        />
                    </FormField>

                    <FormField label="설명">
                        <TextField
                            size="small" value={form.description}
                            onChange={e => set("description", e.target.value)}
                            multiline rows={10} fullWidth
                            placeholder={isOwner ? "일정에 대한 상세 내용을 입력하세요" : ""}
                            {...readOnlyProps(!isOwner)}
                        />
                    </FormField>

                    {/*
                      참여자(공유 대상) — 회의 참석자처럼 직원을 걸어둔다. 소유권과 무관한 표시용.
                      선택된 사람은 입력창 안에 칩으로 몰아넣지 않고 <b>아래 세로 목록(표)</b>에 한 줄에 한 명씩
                      보여준다(요청). 휴가·프로젝트 합성 이벤트에는 참여자 개념이 없어 이 섹션을 숨긴다.
                    */}
                    {!isSynthetic && (
                        <FormField label="참여자 · 공유">
                            <Stack spacing={1}>
                                {isOwner && (
                                    <AppAutocomplete
                                        multiple
                                        options={employees}
                                        getOptionLabel={o => o.name || ""}
                                        // 드롭다운에도 부서·직위를 함께 보여준다(동명이인 구분 + 요청).
                                        secondaryText={o => [o.deptName, o.positionName].filter(Boolean).join(" · ")}
                                        isOptionEqualToValue={(o, v) => o.id === v.id}
                                        value={form.participants}
                                        onChange={(v) => set("participants", v || [])}
                                        renderTag={() => null}   // 선택 결과는 아래 세로 목록에 표로 — 입력창엔 칩을 띄우지 않는다
                                        placeholder="참여자 직원 검색·선택..."
                                        variant="outlined" disableUnderline={false}
                                    />
                                )}

                                {(form.participants || []).length > 0 ? (
                                    <Box sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden" }}>
                                        {form.participants.map((p, idx) => (
                                            <Stack key={p.id ?? idx} direction="row" alignItems="center" spacing={1}
                                                sx={{
                                                    px: 1.5, py: 1,
                                                    bgcolor: idx % 2 ? "#f8fafc" : "#fff",
                                                    borderTop: idx === 0 ? "none" : "1px solid #eef2f6",
                                                }}>
                                                <PersonRoundedIcon sx={{ fontSize: 16, color: "#6366f1", flexShrink: 0 }} />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography noWrap sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                                                        {p.name}
                                                    </Typography>
                                                    {(p.deptName || p.positionName) && (
                                                        <Typography noWrap sx={{ fontSize: 11, color: "#94a3b8" }}>
                                                            {[p.deptName, p.positionName].filter(Boolean).join(" · ")}
                                                        </Typography>
                                                    )}
                                                </Box>
                                                {isOwner && (
                                                    <Tooltip title="참여자 제거">
                                                        <IconButton size="small"
                                                            onClick={() => set("participants", form.participants.filter(x => x.id !== p.id))}>
                                                            <CloseRoundedIcon sx={{ fontSize: 15, color: "#94a3b8" }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Stack>
                                        ))}
                                    </Box>
                                ) : (
                                    <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>지정된 참여자가 없습니다</Typography>
                                )}
                            </Stack>
                        </FormField>
                    )}

                    {isOwner && (
                        <FormControlLabel
                            control={
                                <Switch checked={form.allDay}
                                    onChange={e => set("allDay", e.target.checked)}
                                    size="small" />
                            }
                            label={
                                <Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>종일 일정</Typography>
                                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>특정 날짜 하루 종일</Typography>
                                </Box>
                            }
                        />
                    )}

                    {form.allDay ? (
                        <Stack direction="row" spacing={1}>
                            <FormField label="시작일" required sx={{ flex: 1 }}>
                                <TextField type="date" size="small" value={form.startDate}
                                    onChange={e => setForm(p => applyRangeStartChange(p, e.target.value, { startKey: "startDate", endKey: "endDate", isDate: true }))}
                                    slotProps={{ htmlInput: { readOnly: !isOwner } }}
                                    fullWidth />
                            </FormField>
                            <FormField label="종료일" sx={{ flex: 1 }}>
                                <TextField type="date" size="small" value={form.endDate}
                                    onChange={e => setForm(p => applyRangeEndChange(p, e.target.value, { startKey: "startDate", endKey: "endDate" }))}
                                    slotProps={{ htmlInput: { min: form.startDate, readOnly: !isOwner } }}
                                    fullWidth />
                            </FormField>
                        </Stack>
                    ) : (
                        <Stack direction="row" spacing={1}>
                            <FormField label="시작일시" required sx={{ flex: 1 }}>
                                <TextField type="datetime-local" size="small" value={form.startAt}
                                    onChange={e => setForm(p => applyRangeStartChange(p, e.target.value, { startKey: "startAt", endKey: "endAt" }))}
                                    slotProps={{ htmlInput: { readOnly: !isOwner } }}
                                    fullWidth />
                            </FormField>
                            <FormField label="종료일시" required sx={{ flex: 1 }}>
                                <TextField type="datetime-local" size="small" value={form.endAt}
                                    onChange={e => setForm(p => applyRangeEndChange(p, e.target.value, { startKey: "startAt", endKey: "endAt" }))}
                                    slotProps={{ htmlInput: { min: form.startAt, readOnly: !isOwner } }}
                                    fullWidth />
                            </FormField>
                        </Stack>
                    )}

                    {isOwner ? (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={!!form.deptId}
                                    onChange={e => set("deptId", e.target.checked ? String(myDeptId || "") : "")}
                                    size="small" disabled={!myDeptId}
                                    sx={{ color: "#2563eb", "&.Mui-checked": { color: "#2563eb" } }}
                                />
                            }
                            label={
                                <Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                                        우리 부서 일정으로 등록
                                    </Typography>
                                    {myDeptName ? (
                                        <Typography sx={{ fontSize: 11, color: "#64748b" }}>
                                            {myDeptName} 팀원 전체에게 공유됩니다
                                        </Typography>
                                    ) : (
                                        <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>
                                            소속 부서가 없으면 사용할 수 없습니다
                                        </Typography>
                                    )}
                                </Box>
                            }
                            sx={{ alignItems: "flex-start", m: 0 }}
                        />
                    ) : (
                        event?.deptName && (
                            <Stack direction="row" alignItems="center" spacing={1}
                                sx={{ px: 1.5, py: 1, bgcolor: "#f0f9ff", borderRadius: 1.5, border: "1px solid #bae6fd" }}>
                                <Chip label="부서 일정" size="small"
                                    sx={{ bgcolor: "#0ea5e9", color: "#fff", fontWeight: 700, fontSize: 11, borderRadius: 1 }} />
                                <Typography sx={{ fontSize: 12, color: "#0369a1", fontWeight: 600 }}>
                                    {event.deptName}
                                </Typography>
                            </Stack>
                        )
                    )}

                    {isEdit && event?.createdByName && (
                        <Stack direction="row" alignItems="center" spacing={0.5}
                            sx={{ bgcolor: "#f8fafc", borderRadius: 1.5, px: 1.5, py: 1, border: "1px solid #e2e8f0" }}>
                            <PersonRoundedIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
                            <Typography sx={{ fontSize: 12, color: "#64748b" }}>
                                등록자: <strong>{event.createdByName}</strong>
                                {event.employeeName && event.employeeName !== event.createdByName
                                    ? ` · 대상: ${event.employeeName}` : ""}
                            </Typography>
                        </Stack>
                    )}

                    <Divider />

                    {/* 첨부파일 */}
                    <Box>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <AttachFileRoundedIcon sx={{ fontSize: 15, color: "#64748b" }} />
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>첨부파일</Typography>
                                {(attachments.length + pendingFiles.length) > 0 && (
                                    <Chip label={attachments.length + pendingFiles.length} size="small"
                                        sx={{ height: 18, fontSize: 10, bgcolor: "#dbeafe", color: "#1d4ed8", fontWeight: 700 }} />
                                )}
                            </Stack>
                            {isOwner && (
                                <>
                                    <input ref={fileInputRef} type="file" multiple style={{ display: "none" }} onChange={handleFileSelect} />
                                    <ActionButton variant="secondary"
                                        startIcon={<AttachFileRoundedIcon sx={{ fontSize: 16 }} />}
                                        onClick={() => fileInputRef.current?.click()}
                                        disabled={saving}
                                        label="파일 선택"
                                        sx={{ fontSize: 12 }} />
                                </>
                            )}
                        </Stack>

                        {/* 기존 첨부 — 시스템 공통 뷰어(이미지 썸네일·미리보기·다운로드). 작성자면 삭제 버튼 노출.
                            예전엔 이 목록을 손으로 재구현해 이미지 썸네일·미리보기(previewAttachment)를 못 썼다. */}
                        <AttachmentPreviewList
                            attachments={attachments}
                            previewFn={calendarApi.previewAttachment}
                            onDelete={isOwner && !saving ? handleDeleteAttach : undefined}
                            showUploader
                            sx={{ mb: pendingFiles.length > 0 ? 1 : 0 }}
                        />

                        {pendingFiles.length > 0 && (
                            <Stack spacing={0.5}>
                                {pendingFiles.map((f, i) => (
                                    <Box key={i} sx={{
                                        display: "flex", alignItems: "center",
                                        px: 1.5, py: 0.8, borderRadius: 1.5,
                                        border: "1px dashed #2563eb", bgcolor: "#eff6ff", gap: 1,
                                    }}>
                                        <InsertDriveFileRoundedIcon sx={{ fontSize: 16, color: "#3b82f6", flexShrink: 0 }} />
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {f.name}
                                            </Typography>
                                            <Typography sx={{ fontSize: 11, color: "#3b82f6" }}>
                                                {fmtSize(f.size)} · 저장 시 업로드
                                            </Typography>
                                        </Box>
                                        <IconButton size="small" onClick={() => removePending(i)} disabled={saving}>
                                            <CloseRoundedIcon sx={{ fontSize: 14 }} />
                                        </IconButton>
                                    </Box>
                                ))}
                            </Stack>
                        )}

                        {attachments.length === 0 && pendingFiles.length === 0 && (
                            <Typography sx={{ fontSize: 12, color: "#cbd5e1", textAlign: "center", py: 1 }}>
                                첨부파일 없음
                            </Typography>
                        )}
                    </Box>
                </Stack>
        </FormModal>
    );
}
